import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { RevenueSummaryMetrics } from '@monitoring-panel/types';
import { formatNumber } from '@/lib/utils';
import {
  ADVIA_CHART_OPTS,
  ADVIA_CHART_TOKENS,
  ADVIA_ECHARTS_THEME,
  OPPORTUNITY_TYPE_COLORS,
  chartGradient,
  cssGradient,
  darkStop,
  type SeriesColor,
  tooltipRow,
  tooltipTitle,
} from '@/lib/advia-charts';

interface OpportunityTypePieChartProps {
  data: RevenueSummaryMetrics['opportunityTypeBreakdown'];
}

// Colors for opportunity types
const COLORS = OPPORTUNITY_TYPE_COLORS;

/** Donut slices take the series gradient; the legend dot its darker stop. */
function sliceStyle(series: SeriesColor) {
  return { color: chartGradient(series) };
}

export function OpportunityTypePieChart({ data }: OpportunityTypePieChartProps) {
  const chartOption: EChartsOption = useMemo(() => {
    if (!data) return {};

    const pieData = [
      {
        name: 'Branding (Actual)',
        value: data.branding.actual,
        itemStyle: sliceStyle(COLORS.branding.actual),
        legendColor: darkStop(COLORS.branding.actual),
      },
      {
        name: 'Branding (Forecasted)',
        value: data.branding.forecasted,
        itemStyle: sliceStyle(COLORS.branding.forecasted),
        legendColor: darkStop(COLORS.branding.forecasted),
      },
      {
        name: 'Performance (Actual)',
        value: data.performance.actual,
        itemStyle: sliceStyle(COLORS.performance.actual),
        legendColor: darkStop(COLORS.performance.actual),
      },
      {
        name: 'Performance (Forecasted)',
        value: data.performance.forecasted,
        itemStyle: sliceStyle(COLORS.performance.forecasted),
        legendColor: darkStop(COLORS.performance.forecasted),
      },
      {
        name: 'Youtube (Actual)',
        value: data.youtube.actual,
        itemStyle: sliceStyle(COLORS.youtube.actual),
        legendColor: darkStop(COLORS.youtube.actual),
      },
      {
        name: 'Youtube (Forecasted)',
        value: data.youtube.forecasted,
        itemStyle: sliceStyle(COLORS.youtube.forecasted),
        legendColor: darkStop(COLORS.youtube.forecasted),
      },
      {
        name: 'Undefined (Actual)',
        value: data.undefined.actual,
        itemStyle: sliceStyle(COLORS.undefined.actual),
        legendColor: darkStop(COLORS.undefined.actual),
      },
      {
        name: 'Undefined (Forecasted)',
        value: data.undefined.forecasted,
        itemStyle: sliceStyle(COLORS.undefined.forecasted),
        legendColor: darkStop(COLORS.undefined.forecasted),
      },
    ].filter((item) => item.value > 0);

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const name = params.name;
          const value = params.value;
          const percent = params.percent || 0;

          return (
            tooltipTitle(name) +
            tooltipRow('Revenue', formatNumber(value, 'euro')) +
            tooltipRow('Share', formatNumber(percent, 'percentage', 1))
          );
        },
      },
      legend: {
        orient: 'vertical',
        right: 20,
        top: 'center',
        itemGap: 12,
        data: pieData.map((item) => ({ name: item.name, itemStyle: { color: item.legendColor } })),
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['40%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: ADVIA_CHART_TOKENS.ivory,
            borderWidth: 2,
          },
          label: {
            show: false,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(41, 41, 41, 0.2)',
            },
            label: {
              show: true,
              fontSize: 15,
              fontWeight: 'bold',
            },
          },
          data: pieData,
        },
      ],
    };
  }, [data]);

  const hasData =
    data &&
    (data.branding.total > 0 ||
      data.performance.total > 0 ||
      data.youtube.total > 0 ||
      data.undefined.total > 0);

  if (!hasData) {
    return (
      <div className="adv-card adv-state">
        <p className="adv-state__desc">No opportunity type data available</p>
      </div>
    );
  }

  return (
    <div className="adv-card p-6">
      <div className="mb-4">
        <h2 className="adv-section-title">
          Revenue by Opportunity Type
        </h2>
        <p className="adv-section-desc mt-1">
          Breakdown by Branding, Performance, and Youtube campaigns
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-6 mb-4">
        <div className="flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: darkStop(COLORS.branding.actual) }}
          />
          <span className="text-adv-body-s text-adv-secondary">
            Branding:{' '}
            <span className="adv-num font-bold text-adv-primary">
              {formatNumber(data.branding.total, 'euro')}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: darkStop(COLORS.performance.actual) }}
          />
          <span className="text-adv-body-s text-adv-secondary">
            Performance:{' '}
            <span className="adv-num font-bold text-adv-primary">
              {formatNumber(data.performance.total, 'euro')}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: darkStop(COLORS.youtube.actual) }}
          />
          <span className="text-adv-body-s text-adv-secondary">
            Youtube:{' '}
            <span className="adv-num font-bold text-adv-primary">
              {formatNumber(data.youtube.total, 'euro')}
            </span>
          </span>
        </div>
        {data.undefined.total > 0 && (
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: darkStop(COLORS.undefined.actual) }}
            />
            <span className="text-adv-body-s text-adv-secondary">
              Undefined:{' '}
              <span className="adv-num font-bold text-adv-primary">
                {formatNumber(data.undefined.total, 'euro')}
              </span>
            </span>
          </div>
        )}
      </div>
      <ReactECharts
        option={chartOption}
        style={{ height: 350 }}
        theme={ADVIA_ECHARTS_THEME}
        opts={ADVIA_CHART_OPTS}
      />
      <div className="mt-4 flex items-center justify-center gap-8 text-adv-body-s text-adv-secondary">
        <div className="flex items-center gap-2">
          <div className="w-8 h-2 rounded-full" style={{ background: cssGradient(COLORS.branding.actual) }} />
          <span>Actual</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-2 rounded-full" style={{ background: cssGradient(COLORS.branding.forecasted) }} />
          <span>Forecasted</span>
        </div>
      </div>
    </div>
  );
}
